import { Empty, ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useStores, type Order } from './data';
import { NewOrderDialog, ORDER_SOURCES } from './OrderForm';
import { SourceLabel } from './ui';

const STATUS_TONE: Record<string, string> = {
  placed: 'badge',
  paid: 'badge',
  fulfilled: 'badge-success',
  delivered: 'badge-success',
  cancelled: 'badge-neutral',
  returned: 'badge-warning',
};

export function OrderStatus({ value }: { value: string }) {
  const { t } = useTranslation();
  return (
    <span className={`badge ${STATUS_TONE[value] ?? 'badge-neutral'}`}>
      {t(`retail.orderStatus.${value}`)}
    </span>
  );
}

/** Sales from every store, the website and every chat channel, with the campaign each one is credited to. */
export function OrdersPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { can, session } = useAuth();
  const stores = useStores();
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [storeId, setStoreId] = useState('');
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(50);
  const params = new URLSearchParams(
    Object.entries({ q, source, storeId, limit: String(limit) }).filter(([, v]) => v),
  );
  const list = useQuery({
    queryKey: ['orders', q, source, storeId, limit],
    placeholderData: (prev) => prev,
    queryFn: () =>
      api<{ items: Order[]; totals: { orders: number; revenueMinor: string } }>(`/v1/orders?${params}`),
  });
  const currency = session!.tenant.currency;
  const ar = i18n.language === 'ar';
  return (
    <>
      <PageHead
        eyebrow={t('nav.sales')}
        title={t('retail.orders.title')}
        subtitle={t('retail.orders.subtitle')}
        actions={
          can('commerce:write') && (
            <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-order">
              <Plus size={16} />
              {t('retail.order.new')}
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <input
            className="input"
            type="search"
            placeholder={t('retail.orders.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select
            value={source}
            onChange={setSource}
            aria-label={t('retail.order.source')}
            options={[
              { value: '', label: t('retail.orders.allSources') },
              ...ORDER_SOURCES.map((s) => ({ value: s, label: t(`retail.source.${s}`) })),
            ]}
          />
          <Select
            value={storeId}
            onChange={setStoreId}
            aria-label={t('retail.order.store')}
            options={[
              { value: '', label: t('retail.orders.allStores') },
              ...(stores.data?.items ?? []).map((s) => ({ value: s.id, label: (ar && s.nameAr) || s.name })),
            ]}
          />
          {list.data && (
            <span className="faint toolbar-note" data-testid="orders-total">
              {t('retail.orders.totals', {
                count: list.data.totals.orders,
                value: fmt.money(Number(list.data.totals.revenueMinor), currency),
              })}
            </span>
          )}
        </div>
        {list.isLoading && <Loading />}
        {list.isError && <ErrorState error={list.error} />}
        {list.data?.items.length === 0 && <Empty text={t('retail.orders.empty')} />}
        {!!list.data?.items.length && (
          <div className="table-wrap">
            <table className="table table-responsive" data-testid="orders-table">
              <thead>
                <tr>
                  <th>{t('retail.col.order')}</th>
                  <th>{t('retail.col.customer')}</th>
                  <th>{t('retail.col.items')}</th>
                  <th>{t('retail.col.where')}</th>
                  <th>{t('retail.col.campaign')}</th>
                  <th>{t('retail.col.status')}</th>
                  <th className="num">{t('retail.col.total')}</th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((o) => (
                  <tr key={o.id}>
                    <td data-label={t('retail.col.order')}>
                      <span className="mono">{o.number}</span>
                      <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                        {fmt.dateTime(o.placedAt)}
                      </span>
                    </td>
                    <td data-label={t('retail.col.customer')}>
                      {o.personId ? (
                        <Link to={`/people/${o.personId}`} dir="auto">
                          {o.personName}
                        </Link>
                      ) : (
                        <span className="faint">{t('retail.orders.walkIn')}</span>
                      )}
                    </td>
                    <td data-label={t('retail.col.items')} dir="auto" className="order-items">
                      {(o.items ?? [])
                        .map((i) => `${i.quantity > 1 ? `${i.quantity} × ` : ''}${i.name}`)
                        .join(', ')}
                    </td>
                    <td data-label={t('retail.col.where')}>
                      <SourceLabel source={o.source} />
                      {o.storeName && o.source === 'store' && (
                        <span
                          className="faint"
                          style={{ display: 'block', fontSize: 'var(--t-xs)' }}
                          dir="auto"
                        >
                          {(ar && o.storeNameAr) || o.storeName}
                        </span>
                      )}
                    </td>
                    <td data-label={t('retail.col.campaign')}>
                      {o.campaignId ? (
                        <Link
                          to={`/campaigns/${o.campaignId}`}
                          title={t(`retail.reason.${o.attributionReason}`)}
                          dir="auto"
                        >
                          {o.campaignName}
                        </Link>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td data-label={t('retail.col.status')}>
                      <OrderStatus value={o.status} />
                    </td>
                    <td data-label={t('retail.col.total')} className="num">
                      {fmt.money(Number(o.totalMinor), o.currency)}
                      {o.couponCode && (
                        <span className="faint mono" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                          {o.couponCode}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {list.data && list.data.items.length >= limit && limit < 500 && (
          <div className="card-pad">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setLimit((l) => l + 100)}
              data-testid="more-orders"
            >
              {t('retail.orders.more')}
            </button>
          </div>
        )}
      </section>
      {open && <NewOrderDialog onClose={() => setOpen(false)} />}
    </>
  );
}
